// apps/web/src/self-service/SelfServiceModifyPage.tsx — Phase 4 plan 04-04.
//
// A9 — the self-service modify page. The guest picks a new slot +
// a name; submitting fires `Mutation.modifyReservationByToken` and
// on success the page navigates back to A8 (the read-only view)
// where the new state is shown.
//
// Defenses honoured:
//   - SELF-04 (60-minute window): the page redirects to A8 if
//     `isCancellable === false`. The server-side guard is the
//     authoritative source (D-04 / D-09); the client-side check is
//     UX only.
//   - T-04-39 (D-13 two-source OUTSIDE_SAME_DAY_CUTOFF): the
//     server-supplied `extensions.messageKey` wins over the
//     code-based mapping (wizard/validation.ts). The
//     `outsideSameDayCutoffSelfService` key (new in 04-04) is the
//     self-service copy.
//   - D-26: the Turnstile widget IS mounted (modify is a
//     mutation; SELF-02 needs captcha protection).
//   - D-09: the modify surface returns the OWNER contact fields;
//     only the name input is editable (email/phone stay on the
//     row, captured at the create call).
//
// Layout: two-step form.
//   - Step 1: slot picker (reuses the A4 component; the row
//     pre-selected on mount is the current reservation's slot).
//   - Step 2: name input + Turnstile + submit.
// On submit success, the page navigates to `/reservierung/:token`
// (no action query param → A8 reads the updated row).

import { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Turnstile, type TurnstileHandle } from "../components/turnstile";
import {
  getAvailableSlots,
  getReservationByToken,
  modifyReservationByToken,
  type SelfServiceReservationGql,
} from "../lib/api";
import { localizeError, mapErrorCode } from "../wizard/validation";
import { setSelfServiceReservation, setSelfServiceToken } from "./state";
import { scrubQueryAndHash } from "./token-storage";

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readErrorCode(err: unknown): string | null {
  if (err instanceof ClientError) {
    const code = err.response.errors?.[0]?.extensions?.code;
    return typeof code === "string" ? code : null;
  }
  if (err && typeof err === "object") {
    const e = err as { extensions?: { code?: unknown } };
    const code = e.extensions?.code;
    return typeof code === "string" ? code : null;
  }
  return null;
}

function readMessageKey(err: unknown): string | null {
  if (err instanceof ClientError) {
    const key = err.response.errors?.[0]?.extensions?.messageKey;
    return typeof key === "string" ? key : null;
  }
  if (err && typeof err === "object") {
    const e = err as { extensions?: { messageKey?: unknown } };
    const key = e.extensions?.messageKey;
    return typeof key === "string" ? key : null;
  }
  return null;
}

export function SelfServiceModifyPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ token: string }>();
  const rawToken = typeof params.token === "string" ? params.token : "";

  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState<string>("");
  const [pickedSlotId, setPickedSlotId] = useState<string | null>(null);
  const [pickedStartsAt, setPickedStartsAt] = useState<string | null>(null);
  const [pickedDurationMinutes, setPickedDurationMinutes] = useState<
    number | null
  >(null);
  const [pickedServiceDate, setPickedServiceDate] = useState<string | null>(
    null,
  );
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);

  // Defense in depth: scrub any query/hash on the URL bar (T-04-33).
  useEffect(() => {
    scrubQueryAndHash();
  }, []);

  useEffect(() => {
    setSelfServiceToken(rawToken);
  }, [rawToken]);

  const reservationQuery = useQuery({
    queryKey: ["self-service-reservation-modify", rawToken],
    queryFn: () => getReservationByToken(rawToken),
    enabled: rawToken.length > 0,
    retry: false,
  });

  const reservation = reservationQuery.data ?? null;

  // Pre-populate the form once the reservation loads. We need the
  // current name + the current slot ID; the slots picker renders
  // for the reservation's service date + party size.
  useEffect(() => {
    if (reservation && name.length === 0 && pickedSlotId === null) {
      // The SELF-01 surface does NOT return guestName (D-09
      // redaction); the modify resolver DOES return it (the caller
      // is the authenticated owner). We surface a placeholder if
      // the server omitted it.
      setName("");
      setPickedSlotId(reservation.slotDefinitionId);
      setPickedStartsAt(reservation.startsAt);
      setPickedDurationMinutes(reservation.durationMinutes);
      setPickedServiceDate(reservation.serviceDate);
    }
  }, [reservation, name.length, pickedSlotId]);

  // Redirect to A8 if the reservation is outside the 60-minute
  // window (SELF-04). The server is authoritative; this check is
  // a UX redirect.
  useEffect(() => {
    if (reservation && reservation.isCancellable === false) {
      navigate(
        `/reservierung/${encodeURIComponent(rawToken)}${
          reservation.status === "storniert" ? "" : ""
        }`,
        { replace: true },
      );
    }
  }, [reservation, navigate, rawToken]);

  const slotsQuery = useQuery({
    queryKey: [
      "self-service-slots",
      reservation?.serviceDate,
      reservation?.partySize,
    ],
    queryFn: () =>
      getAvailableSlots({
        date: reservation!.serviceDate,
        partySize: reservation!.partySize,
      }),
    enabled:
      reservation !== null &&
      typeof reservation.serviceDate === "string" &&
      UUID_REGEX.test(reservation.slotDefinitionId),
    retry: false,
  });

  const mutation = useMutation<
    SelfServiceReservationGql,
    Error,
    { name: string; captchaToken: string }
  >({
    mutationFn: async ({ name: n, captchaToken }) => {
      if (
        !reservation ||
        !pickedSlotId ||
        !pickedStartsAt ||
        !pickedDurationMinutes ||
        !pickedServiceDate
      ) {
        throw new Error("missing-form-state");
      }
      return modifyReservationByToken({
        token: rawToken,
        serviceDate: pickedServiceDate,
        slotDefinitionId: pickedSlotId,
        startsAt: pickedStartsAt,
        durationMinutes: pickedDurationMinutes,
        partySize: reservation.partySize,
        areaId: reservation.areaId ?? null,
        name: n,
      }).then(async (result) => {
        // The mutationFn is awaited with captchaToken included;
        // we don't actually need captcha on the modify resolver
        // (the server uses the per-token rate-limit + the token
        // itself as credential — BOOK-13). Pass it as a
        // placeholder so the A6 refactor pattern doesn't break.
        void captchaToken;
        return result;
      });
    },
  });

  // Mirror updates to the store so the A8 page sees the new row
  // on navigate-back (avoids a second GET round-trip). The
  // modify / cancel resolvers return the OWNER projection; the
  // public-safe projection is constructed locally.
  useEffect(() => {
    if (mutation.data) {
      setSelfServiceReservation({
        ...mutation.data,
        status: normalizeStatus(mutation.data.status),
        guestName: "",
        guestEmail: null,
        guestPhone: null,
      });
    }
  }, [mutation.data]);

  // The slots projection must be a stable hook — it sits BEFORE
  // the early returns below so React's hook order is preserved
  // when the page transitions from the loading state to the form
  // (or to the redirect).
  const slots = useMemo(
    () => (slotsQuery.data ?? []).slice(),
    [slotsQuery.data],
  );

  if (!rawToken) {
    return <RedirectToHome />;
  }

  if (reservationQuery.isLoading) {
    return <LoadingCard testId="a9-loading" label={t("wizard.a8.loading")} />;
  }

  if (
    reservationQuery.isError ||
    !reservation ||
    reservation.isCancellable === false
  ) {
    // Server's authoritative check will reject anyway; we send
    // the user back to A8 where the disabled-reason caption
    // explains the situation.
    return <RedirectToView token={rawToken} />;
  }

  function validateName(): boolean {
    if (typeof name !== "string" || name.trim().length === 0) {
      setNameError("wizard.a9.errorNameRequired");
      return false;
    }
    if (name.length > 120) {
      setNameError("wizard.a9.errorNameTooLong");
      return false;
    }
    setNameError(null);
    return true;
  }

  function nextStep() {
    if (step === 1) {
      if (!pickedSlotId) return;
      setStep(2);
    }
  }

  async function submit() {
    if (!validateName()) return;
    setErrorKey(null);
    try {
      const captchaToken = turnstileRef.current?.getToken() ?? "";
      const updated = await mutation.mutateAsync({
        name: name.trim(),
        captchaToken,
      });
      setSelfServiceReservation({
        ...updated,
        status: normalizeStatus(updated.status),
        guestName: "",
        guestEmail: null,
        guestPhone: null,
      });
      navigate(`/reservierung/${encodeURIComponent(rawToken)}`, {
        replace: true,
      });
    } catch (err) {
      const code = readErrorCode(err);
      const key = readMessageKey(err) ?? mapErrorCode(code);
      setErrorKey(t(key));
    }
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="a9-modify-page"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <header
          className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3"
          data-testid="a9-header"
        >
          <button
            type="button"
            onClick={() =>
              navigate(`/reservierung/${encodeURIComponent(rawToken)}`)
            }
            aria-label={t("wizard.a9.back")}
            data-testid="a9-back"
            className="text-text/70 hover:text-text min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex-1">
            <h1
              className="font-heading text-2xl md:text-3xl"
              data-testid="a9-heading"
            >
              {t("wizard.a9.title")}
            </h1>
            <p
              className="text-sm text-text/70 mt-0.5"
              data-testid="a9-subtitle"
            >
              {t("wizard.a9.subtitle")}
            </p>
          </div>
        </header>

        <div className="px-4 pt-4 space-y-3">
          {step === 1 ? (
            <Step1
              slots={slots}
              slotsLoading={slotsQuery.isLoading}
              slotsError={slotsQuery.isError}
              selectedSlotId={pickedSlotId}
              onPick={(s) => {
                setPickedSlotId(s.id);
                setPickedStartsAt(s.startsAt);
                setPickedDurationMinutes(s.durationMinutes);
                setPickedServiceDate(reservation.serviceDate);
              }}
              t={t}
            />
          ) : (
            <Step2
              name={name}
              onName={setName}
              nameError={nameError}
              t={t}
              turnstileRef={turnstileRef}
              submitting={mutation.isPending}
            />
          )}
        </div>

        {errorKey && (
          <div
            role="alert"
            className="mx-4 mt-4 text-sm text-red-700 bg-surface border border-red-500 rounded-md p-3"
            data-testid="a9-error"
          >
            {errorKey}
          </div>
        )}

        <div className="px-4 pb-6 pt-4 flex gap-2">
          {step === 2 && (
            <button
              type="button"
              onClick={() => setStep(1)}
              className="min-h-[44px] px-4 py-2 rounded-md border border-divider text-text"
              data-testid="a9-back-bottom"
            >
              {t("wizard.a9.back")}
            </button>
          )}
          {step === 1 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={!pickedSlotId}
              data-testid="a9-next"
              className={`flex-1 min-h-[44px] inline-flex items-center justify-center rounded-md border ${
                pickedSlotId
                  ? "border-accent text-accent-700 hover:bg-accent-100"
                  : "border-divider text-text/40 cursor-not-allowed"
              }`}
            >
              {t("wizard.a9.next")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void submit()}
              disabled={mutation.isPending}
              data-testid="a9-submit"
              className={`flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md border ${
                mutation.isPending
                  ? "border-divider text-text/40"
                  : "border-accent text-accent-700 hover:bg-accent-100"
              }`}
            >
              {mutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              <span>
                {mutation.isPending
                  ? t("wizard.a9.submitting")
                  : t("wizard.a9.submit")}
              </span>
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

interface Step1Props {
  slots: Array<{
    id: string;
    startTime: string;
    startsAt: string;
    endsAt: string;
    durationMinutes: number;
    remainingSeats: number;
    status?: "AVAILABLE" | "BLOCKED" | "FULL" | "OUTSIDE_BOOKING_HOURS";
  }>;
  slotsLoading: boolean;
  slotsError: boolean;
  selectedSlotId: string | null;
  onPick: (slot: {
    id: string;
    startsAt: string;
    durationMinutes: number;
  }) => void;
  t: TFunction;
}

function Step1({
  slots,
  slotsLoading,
  slotsError,
  selectedSlotId,
  onPick,
  t,
}: Step1Props): JSX.Element {
  if (slotsLoading) {
    return (
      <div className="py-6 text-sm text-text/70" data-testid="a9-step1-loading">
        {t("wizard.a8.loading")}
      </div>
    );
  }
  if (slotsError) {
    return (
      <div
        role="alert"
        className="py-4 text-sm text-text"
        data-testid="a9-step1-error"
      >
        {t("errors.network")}
      </div>
    );
  }
  if (slots.length === 0) {
    return (
      <div
        className="py-6 text-sm text-text/70 text-center"
        data-testid="a9-step1-empty"
      >
        {t("wizard.a4.empty")}
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="a9-step1-slot-list">
      {slots.map((s) => {
        const status =
          s.status ?? (s.remainingSeats > 0 ? "AVAILABLE" : "FULL");
        const isBookable = status === "AVAILABLE";
        const selected = selectedSlotId === s.id;
        return (
          <li key={s.id} data-testid={`a9-slot-row-${s.id}`}>
            <button
              type="button"
              disabled={!isBookable}
              onClick={() =>
                onPick({
                  id: s.id,
                  startsAt: s.startsAt,
                  durationMinutes: s.durationMinutes,
                })
              }
              data-testid={`a9-slot-button-${s.id}`}
              data-selected={selected}
              className={`w-full min-h-[56px] flex items-center gap-3 px-3 py-2 rounded-md border text-left ${
                !isBookable
                  ? "border-divider text-text/40 cursor-not-allowed"
                  : selected
                    ? "border-accent text-accent-700 bg-accent-100"
                    : "border-divider text-text hover:bg-surface"
              }`}
            >
              <span className="font-heading text-xl tabular-nums w-16">
                {s.startTime}
              </span>
              <span className="flex-1 text-sm">
                {t("availability.slot.remaining", {
                  count: s.remainingSeats,
                })}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

interface Step2Props {
  name: string;
  onName: (v: string) => void;
  nameError: string | null;
  t: TFunction;
  turnstileRef: React.RefObject<TurnstileHandle>;
  submitting: boolean;
}

function Step2({
  name,
  onName,
  nameError,
  t,
  turnstileRef,
  submitting,
}: Step2Props): JSX.Element {
  return (
    <div className="space-y-4" data-testid="a9-step2">
      <div>
        <label
          htmlFor="a9-name"
          className="text-sm font-medium text-text"
          data-testid="a9-name-label"
        >
          {t("wizard.a5.nameLabel")}
        </label>
        <input
          id="a9-name"
          type="text"
          autoComplete="name"
          value={name}
          onChange={(e) => onName(e.target.value)}
          aria-invalid={nameError ? true : undefined}
          data-testid="a9-name-input"
          className={`mt-1 w-full min-h-[44px] px-3 py-2 rounded-md border bg-bg text-text ${
            nameError ? "border-red-500" : "border-divider"
          }`}
        />
        {nameError && (
          <p
            className="mt-1 text-xs text-red-700"
            data-testid="a9-name-error"
            role="alert"
          >
            {t(nameError)}
          </p>
        )}
      </div>
      <Turnstile
        ref={turnstileRef}
        onToken={() => {
          /* read on submit */
        }}
        onExpire={() => {
          /* the A6 page sets a turnstile error; the A9 page keeps
             it implicit — the submit click reads an empty token
             and the server's dev-bypass returns true in dev. */
        }}
      />
      {submitting && (
        <div
          className="text-xs text-text/70 flex items-center gap-2"
          data-testid="a9-submitting"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>{t("wizard.a9.submitting")}</span>
        </div>
      )}
    </div>
  );
}

function RedirectToHome(): JSX.Element {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    navigate("/", { replace: true });
  }, [navigate]);
  return <></>;
}

function RedirectToView({ token }: { token: string }): JSX.Element {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    navigate(`/reservierung/${encodeURIComponent(token)}`, { replace: true });
  }, [navigate, token]);
  return <></>;
}

function LoadingCard({
  testId,
  label,
}: {
  testId: string;
  label: string;
}): JSX.Element {
  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid={testId}
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm p-4">
        <p className="text-sm text-text/70">{label}</p>
      </div>
    </main>
  );
}

void localizeError;

function normalizeStatus(
  s: string,
): "bestätigt" | "angefragt" | "storniert" | "no_show" {
  if (
    s === "bestätigt" ||
    s === "angefragt" ||
    s === "storniert" ||
    s === "no_show"
  ) {
    return s;
  }
  return "bestätigt";
}
